import React, { createContext, useContext, useState, useEffect } from 'react';
import { ROLES } from '../constants/roles';
import { 
  hasPermission, 
  verifyTokenAndGetUser, 
  saveAuthData, 
  clearAuthData, 
  mapBackendRoleToFrontend, 
  calculatePermissions,
  refreshAuthToken
} from '../utils/authUtils';
import axios from 'axios';
import api, { authEvents, clearCache } from '../utils/api';
import config from '../config';
import { apiService, tokenManager, safeApiCall } from '../utils/api';
import authService from '../services/auth';
import { getCsrfToken } from '../utils/security';
import { userApi } from '../utils/api';

// デバッグログは削除

// 認証コンテキスト作成
const AuthContext = createContext();

// リフレッシュトークンリクエスト中かどうかのフラグ
let isRefreshing = false;
// 保留中のリクエスト
let refreshSubscribers = [];

// リフレッシュトークン処理完了後に保留リクエストを再実行
const onRefreshComplete = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

// リフレッシュトークン処理中にリクエスト登録
const subscribeToRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// メンテナンスモード状態を取得する関数の最適化
const fetchMaintenanceMode = async () => {
  try {
    console.log('【デバッグ】メンテナンス設定取得開始');
    
    // メンテナンス情報APIの呼び出し - apiServiceを使用
    const response = await apiService.get('/api/settings/maintenance', {
      timeout: 10000, // 10秒タイムアウト
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    console.log('【デバッグ】メンテナンスAPIレスポンス:', response.data);
    
    const serverMaintenanceMode = response.data.enabled === true;
    const endTime = response.data.end_time || '調整中';
    const contactUrl = response.data.contact_url || 'mailto:support@deli-ns.com';
    
    console.log('【デバッグ】メンテナンスモード:', serverMaintenanceMode);
    
    return {
      enabled: serverMaintenanceMode,
      end_time: endTime,
      contact_url: contactUrl
    };
  } catch (error) {
    console.error('【デバッグ】メンテナンス設定取得エラー:', error);
    
    // エラーの詳細情報を表示
    if (error.response) {
      console.error('【デバッグ】サーバーエラー:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('【デバッグ】リクエストエラー:', error.request);
    } else {
      console.error('【デバッグ】設定エラー:', error.message);
    }
    
    // エラー時はメンテナンスモード無効を返す
    return {
      enabled: false,
      end_time: '調整中',
      contact_url: 'mailto:support@deli-ns.com'
    };
  }
};

// AuthProviderコンポーネント
export const AuthProvider = ({ children }) => {
  console.log('【デバッグ】AuthProvider 初期化開始');
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(ROLES.GUEST);
  const [userPermissions, setUserPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenExpired, setTokenExpired] = useState(false);
  // 初期化完了状態を追加
  const [isInitialized, setIsInitialized] = useState(false);
  
  // トークン情報を直接ステートに持つ
  const [token, setToken] = useState(() => {
    // authServiceと同じキーを使用
    const savedToken = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('currentUser');
    const savedExpiry = localStorage.getItem('tokenExpiry');
    
    console.log('【デバッグ】AuthProvider初期化 - localStorage詳細状態:', {
      authToken: savedToken ? `存在する(${savedToken.substring(0, 20)}...)` : 'なし',
      currentUser: savedUser ? JSON.parse(savedUser) : 'なし',
      tokenExpiry: savedExpiry,
      現在時刻: Date.now(),
      期限切れ: savedExpiry && parseInt(savedExpiry) < Date.now()
    });
    
    console.log('【デバッグ】AuthProvider初期化 - localStorageからトークン読み込み:', savedToken ? 'あり' : 'なし');
    return savedToken;
  });
  // isLoggedInを状態として管理
  const [isLoggedIn, setIsLoggedIn] = useState(!!token);
  
  // メンテナンスモード状態
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceEndTime, setMaintenanceEndTime] = useState('調整中');
  const [maintenanceContactUrl, setMaintenanceContactUrl] = useState('mailto:support@example.com');

  console.log('【デバッグ】AuthProvider 初期状態:', {
    isInitialized,
    loading,
    token: token ? '存在する' : 'なし',
    isLoggedIn
  });

  // 初期化関数
  const initAuth = async () => {
    console.log('【デバッグ】認証初期化開始');
    
    // メンテナンス状態を確認
    console.log('【デバッグ】メンテナンス状態確認');
    try {
      const maintenanceData = await fetchMaintenanceMode();
      if (maintenanceData.enabled) {
        setMaintenanceMode(true);
        setMaintenanceEndTime(maintenanceData.end_time);
        setMaintenanceContactUrl(maintenanceData.contact_url);
        setIsInitialized(true);
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('【デバッグ】メンテナンス状態取得エラー:', error);
    }
    
    // LocalStorageからの詳細な状態確認
    console.log('【デバッグ】initAuth - localStorage詳細状態:', {
      authToken: localStorage.getItem('authToken') ? 'あり' : 'なし',
      currentUser: localStorage.getItem('currentUser') ? 'あり' : 'なし',
      tokenExpiry: localStorage.getItem('tokenExpiry'),
      現在時刻: Date.now(),
      期限切れ: localStorage.getItem('tokenExpiry') ? (parseInt(localStorage.getItem('tokenExpiry')) < Date.now()) : null
    });
    
    // トークンの存在を確認
    const savedToken = localStorage.getItem('authToken');
    console.log('【デバッグ】保存されたトークン:', savedToken ? 'あり' : 'なし');
    
    if (savedToken) {
      console.log('【デバッグ】トークンが存在 - 検証開始');
      
      try {
        // トークンを即座にAPIサービスに設定
        api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        apiService.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        console.log('【デバッグ】APIサービスにトークンを設定完了');
        
        // ユーザー情報の取得を試行
        const userData = await fetchUserInfoFromAPI(savedToken);
        
        if (userData) {
          console.log('【デバッグ】ユーザー情報取得成功 - ログイン状態を設定');
          setCurrentUser(userData);
          setToken(savedToken);
          setIsLoggedIn(true);
          
          // ロール設定
          const userRole = userData.role || 'viewer';
          const mappedRole = mapBackendRoleToFrontend(userRole);
          setUserRole(mappedRole);
          
          console.log('【デバッグ】ログイン状態設定完了:', {
            userId: userData.id,
            userRole: mappedRole,
            hasToken: !!savedToken
          });
        } else {
          console.log('【デバッグ】ユーザー情報取得失敗 - localStorageから復元を試行');
          
          // localStorageからユーザー情報を復元
          const savedUser = localStorage.getItem('currentUser');
          if (savedUser) {
            try {
              const parsedUser = JSON.parse(savedUser);
              console.log('【デバッグ】localStorageからユーザー情報を復元');
              setCurrentUser(parsedUser);
              setToken(savedToken);
              setIsLoggedIn(true);
              
              const userRole = parsedUser.role || 'viewer';
              const mappedRole = mapBackendRoleToFrontend(userRole);
              setUserRole(mappedRole);
            } catch (parseError) {
              console.error('【デバッグ】ユーザー情報の復元に失敗:', parseError);
              // 復元に失敗した場合のみゲスト状態に設定
              setCurrentUser(null);
              setToken(null);
              setIsLoggedIn(false);
              setUserRole(ROLES.GUEST);
            }
          } else {
            console.log('【デバッグ】localStorageにユーザー情報なし - ゲスト状態を設定');
            setCurrentUser(null);
            setToken(null);
            setIsLoggedIn(false);
            setUserRole(ROLES.GUEST);
          }
        }
      } catch (error) {
        console.error('【デバッグ】認証初期化エラー:', error);
        
        // エラー時は認証状態をクリア
        localStorage.removeItem('authToken');
        localStorage.removeItem('currentUser');
        delete api.defaults.headers.common['Authorization'];
        delete apiService.defaults.headers.common['Authorization'];
        
        setCurrentUser(null);
        setToken(null);
        setIsLoggedIn(false);
        setUserRole(ROLES.GUEST);
      }
    } else {
      console.log('【デバッグ】トークンなし - ゲスト権限で設定');
      setCurrentUser(null);
      setToken(null);
      setIsLoggedIn(false);
      setUserRole(ROLES.GUEST);
    }
    
    console.log('【デバッグ】認証初期化完了');
    setIsInitialized(true);
    setLoading(false);
  };

  // トークンの有効性を検証する関数
  const validateToken = async (token) => {
    try {
      console.log('【デバッグ】トークン有効性検証開始');
      
      const response = await fetch(`${config.API_BASE_URL}/api/auth/verify`, {
        method: 'GET',
        credentials: 'omit',
        mode: 'cors',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('【デバッグ】トークン検証成功:', data.user ? 'ユーザー情報あり' : 'ユーザー情報なし');
        return true;
      } else {
        console.log('【デバッグ】トークン検証失敗 - ステータス:', response.status);
        return false;
      }
    } catch (error) {
      console.error('【デバッグ】トークン検証エラー:', error);
      return false;
    }
  };

  // APIからユーザー情報を取得する関数
  const fetchUserInfoFromAPI = async (token) => {
    try {
      console.log('【デバッグ】APIからユーザー情報取得開始');
      const response = await apiService.get('/api/auth/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.user) {
        console.log('【デバッグ】API からユーザー情報取得成功:', response.data.user);
        setCurrentUser(response.data.user);
        
        // ローディング状態を解除
        setLoading(false);
        
        // ロードエンドから返ってきたロールを設定
        setUserRoleConsistently(response.data.user);
        return response.data.user;
      } else {
        console.log('【デバッグ】ユーザー情報の取得に失敗 - レスポンス形式が不正');
        return null;
      }
    } catch (error) {
      console.error('【デバッグ】APIユーザー情報取得エラー:', error);
      
      // 401エラーの場合のみ認証情報をクリア（他のエラーはトークンを保持）
      if (error.response && error.response.status === 401) {
        console.log('【デバッグ】401エラー - 認証情報をクリア');
        setToken(null);
        setCurrentUser(null);
        setUserRole(ROLES.GUEST);
        localStorage.removeItem('authToken');
        localStorage.removeItem('currentUser');
      } else {
        console.log('【デバッグ】一時的なエラー - トークンを保持');
      }
      return null;
    }
  };

  // コンポーネントマウント時に認証初期化（一度だけ実行）
  useEffect(() => {
    console.log('【デバッグ】AuthProvider useEffect実行');
    
    // 既に初期化済みの場合は再実行しない
    if (isInitialized) {
      console.log('【デバッグ】既に初期化済み - スキップ');
      return;
    }
    
    // 非同期処理をuseEffect内で直接実行
    const initialize = async () => {
      console.log('【デバッグ】初回初期化実行');
      await initAuth();
      
      // 強制的に再レンダリングを行い、状態を確実に更新
      setIsInitialized(prevState => {
        console.log('【デバッグ】初期化状態を確実に更新:', true);
        return true;
      });
    };
    
    initialize();
    
    // クリーンアップ関数
    return () => {
      console.log('【デバッグ】AuthProvider クリーンアップ');
    };
  }, []); // 依存配列は空のまま（初回のみ実行）

  // カスタムイベントをリッスンしてトークン期限切れを検出
  useEffect(() => {
    const handleTokenExpiredEvent = (event) => {
      console.log('【デバッグ】tokenExpiredカスタムイベント受信:', event.detail);
      
      // 認証状態をクリア
      setCurrentUser(null);
      setToken(null);
      setUserRole(ROLES.GUEST);
      setIsLoggedIn(false);
      setTokenExpired(true);
      setError('セッションの有効期限が切れました。再度ログインしてください。');
      
      // ログイン画面へのリダイレクトが必要な場合
      if (event.detail && event.detail.redirectToLogin) {
        console.log('【デバッグ】ログイン画面へのリダイレクトが要求されました');
        
        // 現在のページがログインページでない場合のみリダイレクト
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          // 現在のURLを保存してログイン後にリダイレクト
          if (!sessionStorage.getItem('redirect_after_login')) {
            sessionStorage.setItem('redirect_after_login', window.location.pathname + window.location.search);
          }
          
          // 少し遅延してからリダイレクトして、状態更新を完了させる
          setTimeout(() => {
            window.location.href = '/login';
          }, 500);
        }
      }
    };
    
    // カスタムイベントリスナーを追加
    if (typeof window !== 'undefined') {
      window.addEventListener('tokenExpired', handleTokenExpiredEvent);
    }
    
    // クリーンアップ関数
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('tokenExpired', handleTokenExpiredEvent);
      }
    };
  }, []);

  // トークン変更を監視して状態を同期（ログイン後の状態管理）
  useEffect(() => {
    if (isInitialized && token) {
      console.log('【デバッグ】トークン変更検出 - 認証状態を同期:', {
        tokenExists: !!token,
        userExists: !!currentUser,
        userRole: userRole
      });
      
      // トークンがあるのにユーザー情報がない場合は、localStorageから復元
      if (!currentUser) {
        const savedUser = localStorage.getItem('currentUser');
        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            console.log('【デバッグ】localStorageからユーザー情報を復元:', userData);
            setCurrentUser(userData);
            
            if (userData.role) {
              const mappedRole = mapBackendRoleToFrontend(userData.role);
              setUserRole(mappedRole);
            }
          } catch (error) {
            console.error('【デバッグ】ユーザー情報復元エラー:', error);
          }
        }
      }
    }
  }, [token, isInitialized]); // tokenとisInitializedの変更を監視

  // トークンの有効期限を定期的にチェック
  useEffect(() => {
    if (!isInitialized || !token) return;
    
    const checkTokenExpiry = () => {
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      const currentTime = Date.now();
      
      if (tokenExpiry && parseInt(tokenExpiry) < currentTime) {
        console.log('【デバッグ】定期チェック: トークン期限切れを検出');
        
        // 認証状態をクリア
        localStorage.removeItem('authToken');
        localStorage.removeItem('currentUser');
        localStorage.removeItem('tokenExpiry');
        setToken(null);
        setCurrentUser(null);
        setUserRole(ROLES.GUEST);
        setIsLoggedIn(false);
        
        // 期限切れ理由を保存
        sessionStorage.setItem('session_expired_reason', 'periodic_check');
        
        // トークン期限切れイベントを発火
        window.dispatchEvent(new CustomEvent('tokenExpired', {
          detail: { reason: 'Token expired - periodic check', redirectToLogin: true }
        }));
      }
    };
    
    // 初回チェック
    checkTokenExpiry();
    
    // 5分間隔でチェック
    const intervalId = setInterval(checkTokenExpiry, 5 * 60 * 1000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [isInitialized, token]);

  // ユーザー情報取得関数
  const fetchUserInfo = async () => {
    console.log('【デバッグ】ユーザー情報取得関数実行');
    const token = tokenManager.getToken();
    
    if (!token) {
      console.log('【デバッグ】トークンなし - ユーザー情報なし');
      setCurrentUser(null);
      setToken(null);
      setLoading(false);
      return;
    }

    try {
      // トークンがあるか確認し、確実にHTTPヘッダーに設定
      if (!apiService.defaults.headers.common['Authorization'] && token) {
        apiService.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      console.log('【デバッグ】ユーザー情報API呼び出し');
      // apiServiceを使用
      const response = await apiService.get('/api/users/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data) {
        console.log('【デバッグ】ユーザー情報取得成功');
        setCurrentUser(response.data);
        setToken(token);
        
        // バックエンドから返ってきたロールを設定
        if (response.data && response.data.role) {
          const mappedRole = mapBackendRoleToFrontend(response.data.role);
          console.log('【デバッグ】ユーザーロール設定:', mappedRole);
          setUserRole(mappedRole);
        } else {
          // バックエンドからロールが返ってこない場合はデフォルトでユーザー権限を設定
          console.log('【デバッグ】デフォルトロール設定: USER');
          setUserRole(ROLES.USER);
        }
      } else {
        throw new Error('ユーザー情報が取得できませんでした');
      }
      
      // ローディング状態を解除
      setLoading(false);
      
      return response.data;
    } catch (error) {
      console.error('【デバッグ】ユーザー情報取得エラー:', error);
      
      // エラーの詳細をチェック
      if (error.response) {
        if (error.response.status === 401) {
          // 【一時的無効化】認証エラー時の自動ログアウトを無効化
          console.log('【デバッグ】401エラー検出 - 自動ログアウトは無効化されています');
          
          // 一時的にコメントアウト：自動ログアウトを防ぐ
          /*
          // 認証エラー - トークンの期限切れなど
          console.log('【デバッグ】認証エラー: トークンをクリアします');
          clearAuthData();
          setToken(null);
          setCurrentUser(null);
          setUserRole(ROLES.GUEST);
          */
        }
      }
      
      setLoading(false);
      return null;
    }
  };

  // ユーザーロールが変更されたときに権限マップを更新
  useEffect(() => {
    // 権限キャッシュをクリア
    clearPermissionCache();
    
    const permissionMap = calculatePermissions(userRole);
    setUserPermissions(permissionMap);
  }, [userRole]);

  // メンテナンスモード設定関数 (管理者のみ使用可能)
  const toggleMaintenanceMode = async (value, endTime, contactUrl) => {
    const newValue = typeof value === 'boolean' ? value : !maintenanceMode;
    const newEndTime = endTime || maintenanceEndTime;
    const newContactUrl = contactUrl || maintenanceContactUrl;
    
    try {
      // サーバーAPIでメンテナンスモード状態を更新 - apiServiceを使用
      await apiService.put('/api/settings/maintenance', { 
        enabled: newValue,
        end_time: newEndTime,
        contact_url: newContactUrl
      }, {
        headers: { 
          'Authorization': `Bearer ${token}` 
        }
      });
      
      setMaintenanceMode(newValue);
      setMaintenanceEndTime(newEndTime);
      setMaintenanceContactUrl(newContactUrl);
      
      return {
        enabled: newValue,
        end_time: newEndTime,
        contact_url: newContactUrl
      };
    } catch (error) {
      console.error('メンテナンスモード設定の更新に失敗しました:', error);
      
      return {
        enabled: maintenanceMode,
        end_time: maintenanceEndTime,
        contact_url: maintenanceContactUrl
      };
    }
  };

  // バッチ処理用のキャッシュを作成
  let maintenanceStateCache = null;
  let lastMaintenanceFetch = 0;
  const MAINTENANCE_CACHE_TTL = 60000; // 1分間キャッシュを有効にする

  // メンテナンスモード状態のバッチ取得（キャッシュ対応）
  const getMaintenanceState = async () => {
    if (maintenanceStateCache && (Date.now() - lastMaintenanceFetch < MAINTENANCE_CACHE_TTL)) {
      // キャッシュが有効ならそれを返す
      return maintenanceStateCache;
    }
    
    // キャッシュが無効なら新しく取得
    const maintenanceState = await fetchMaintenanceMode();
    maintenanceStateCache = maintenanceState;
    lastMaintenanceFetch = Date.now();
    
    return maintenanceState;
  };

  // API認証イベントを監視
  useEffect(() => {
    // トークンリフレッシュリクエストを処理
    const handleTokenRefresh = async (data) => {
      try {
        const newToken = await refreshToken();
        if (newToken) {
          // トークンリフレッシュ成功
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          return newToken;
        } else {
          throw new Error('トークンリフレッシュ失敗');
        }
      } catch (error) {
        logout();
      }
    };

    // 認証エラーを処理
    const handleAuthError = (errorData) => {
      console.log('【デバッグ】handleAuthError呼び出し:', errorData);
      
      // 【一時的無効化】認証エラー時の自動ログアウトを無効化
      console.log('【デバッグ】認証エラー処理 - 自動ログアウトは無効化されています');
      
      /*
      // トークン期限切れフラグをセット
      setTokenExpired(true);
      
      // エラーメッセージがあれば表示
      if (errorData.message) {
        setError(errorData.message);
      } else {
        setError('認証エラーが発生しました。再ログインしてください。');
      }
      
      // すでにログイン画面にいない場合は、ローカルストレージをクリアしてリダイレクト
      if (window.location.pathname !== '/login') {
        // 認証情報をクリア
        clearAuthData();
        tokenManager.clearTokens();
        setToken(null);
        setCurrentUser(null);
        setUserRole(ROLES.GUEST);
        
        // セッションストレージにリダイレクト前のパスを保存
        if (errorData.originalUrl) {
          sessionStorage.setItem('redirect_after_login', window.location.pathname);
        }
        
        // ログインページへリダイレクト
        setTimeout(() => {
          window.location.href = '/login';
        }, 500);
      }
      */
    };

    // イベントリスナー登録
    authEvents.on('tokenRefresh', handleTokenRefresh);
    authEvents.on('authError', handleAuthError);

    // コンポーネントアンマウント時にイベントリスナーを削除
    return () => {
      authEvents.listeners.tokenRefresh = authEvents.listeners.tokenRefresh.filter(
        fn => fn !== handleTokenRefresh
      );
      authEvents.listeners.authError = authEvents.listeners.authError.filter(
        fn => fn !== handleAuthError
      );
    };
  }, [tokenExpired]); // tokenExpiredの状態が変わった時のみ再実行

  // 初期化時にローカルストレージからユーザー情報を読み込む
  useEffect(() => {
    // 古いinitAuth関数を削除 - 新しいinitAuth関数を使用
  }, []);

  // ログイン処理
  const login = async (loginData) => {
    try {
      setLoading(true);
      setError(null);
      
      // プリフライトを回避するため、シンプルなfetchを使用
      const response = await fetch(`${config.API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(loginData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        const error = new Error(errorData.error || 'ログインに失敗しました');
        error.status = response.status;
        error.warning = errorData.warning;
        throw error;
      }

      const data = await response.json();
      
      console.log('【ログイン成功】トークン保存開始:', {
        hasAccessToken: !!data.access_token,
        hasRefreshToken: !!data.refresh_token,
        hasUser: !!data.user,
        tokenStart: data.access_token ? data.access_token.substring(0, 20) + '...' : 'なし'
      });
      
      // トークンとユーザー情報を同期的に保存（統一されたキー名を使用）
      if (data.access_token) {
        localStorage.setItem('authToken', data.access_token);
        console.log('【ログイン成功】authTokenをlocalStorageに保存完了');
      }
      
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
        console.log('【ログイン成功】refreshTokenをlocalStorageに保存完了');
      }
      
      if (data.user) {
        localStorage.setItem('currentUser', JSON.stringify(data.user));
        console.log('【ログイン成功】currentUserをlocalStorageに保存完了');
      }
      
      // 保存完了後にAPI設定を更新
      if (data.access_token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
        apiService.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
        console.log('【ログイン成功】APIサービスのAuthorizationヘッダー設定完了');
      }
      
      // 状態を同期的に更新
      setCurrentUser(data.user);
      setToken(data.access_token);
      setIsLoggedIn(true);
      setUserRoleConsistently(data.user);
      setTokenExpired(false);
      setError(null);
      
      // 保存確認のためのデバッグログ
      console.log('【ログイン成功】最終確認:', {
        localStorageAuthToken: localStorage.getItem('authToken') ? 'あり' : 'なし',
        localStorageCurrentUser: localStorage.getItem('currentUser') ? 'あり' : 'なし',
        apiDefaultsAuth: api.defaults.headers.common['Authorization'] ? 'あり' : 'なし',
        apiServiceAuth: apiService.defaults.headers.common['Authorization'] ? 'あり' : 'なし',
        stateToken: token ? 'あり' : 'なし',
        stateUser: currentUser ? 'あり' : 'なし'
      });
      
      return data;
    } catch (error) {
      console.error('ログインエラー:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // ユーザー登録処理
  const register = async (userData) => {
    setLoading(true);
    setError('');
    
    try {
      let userDataWithToken;
      
      if (userData instanceof FormData) {
        // FormDataの場合は、そのまま使用（CSRFトークンは追加しない）
        userDataWithToken = userData;
      } else {
        // 通常のオブジェクトの場合は、スプレッド演算子を使用
        userDataWithToken = {
        ...userData,
        csrfToken: getCsrfToken()
      };
      }
      
      const result = await authService.register(userDataWithToken);
      
      console.log('AuthContext register result:', result);
      
      // 成功判定を修正 - successがtrueまたはmessageとaccess_tokenが存在する場合
      const isSuccess = result && (
        result.success === true || 
        (result.message && result.access_token) ||
        (result.message && result.message.includes('登録が完了'))
      );
      
      if (isSuccess) {
        // 登録成功時にログイン状態を設定
        if (result.access_token) {
          setToken(result.access_token);
          localStorage.setItem('authToken', result.access_token);
          
          if (result.refresh_token) {
            localStorage.setItem('refreshToken', result.refresh_token);
          }
          
          // APIサービスの認証ヘッダーを更新
          apiService.defaults.headers.common['Authorization'] = `Bearer ${result.access_token}`;
        }
        
        // ユーザー情報を設定
        if (result.user) {
          setCurrentUser(result.user);
          setUserRole(result.user.role || ROLES.VIEWER);
          console.log('ユーザー情報を設定しました:', result.user);
        }
        
        return { 
          success: true, 
          user: result.user,
          access_token: result.access_token 
        };
      } else {
        setError(result.error || 'ユーザー登録に失敗しました');
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('登録エラー:', err);
      setError('ユーザー登録中にエラーが発生しました');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // シンプル高速ログアウト処理
  const logout = async () => {
    console.log('🚪 ログアウト開始');
    
    // 1. 即座に全認証状態をクリア
    setCurrentUser(null);
    setToken(null);
    setIsLoggedIn(false);
    setUserRole(ROLES.GUEST);
    setTokenExpired(false);
    setError(null);
    setLoading(false);
    
    // 2. ストレージとAPIヘッダーをクリア
    const authKeys = ['authToken', 'refreshToken', 'access_token', 'refresh_token', 'currentUser', 'user', 'tokenExpiry'];
    authKeys.forEach(key => localStorage.removeItem(key));
    sessionStorage.clear();
    delete api.defaults.headers.common['Authorization'];
    
    // 3. ブラウザキャッシュクリア（非同期）
    const clearCaches = async () => {
      try {
        if ('serviceWorker' in navigator && 'caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }
        console.log('🗑️ キャッシュクリア完了');
      } catch (error) {
        console.warn('⚠️ キャッシュクリア失敗:', error);
      }
    };
    
    // 4. プッシュ通知送信（非同期・エラー無視）
    const sendLogoutNotification = async () => {
      if (currentUser?.id) {
        try {
          await apiService.post('/api/notifications/enhanced-push', {
            user_id: currentUser.id,
            type: 'logout',
            title: 'ログアウト完了',
            body: 'アカウントからログアウトしました',
            path: '/login',
            data: { type: 'logout', forceNotification: true }
          });
        } catch (error) {
          // 通知失敗は無視
        }
      }
    };
    
    // バックグラウンドで実行（ログアウトを止めない）
    Promise.all([clearCaches(), sendLogoutNotification()]);
    
    console.log('✅ ログアウト完了');
    return { success: true };
  };

  // 認証状態確認
  const checkAuth = () => {
    return !!currentUser;
  };

  // 権限確認
  const hasRole = (role) => {
    return currentUser && currentUser.role === role;
  };

  // 権限チェック用のキャッシュ
  const permissionCache = {};

  // 権限チェック関数
  const checkPermission = (permission) => {
    // キャッシュを確認
    if (permissionCache[permission] !== undefined) {
      return permissionCache[permission];
    }
    
    // 権限のチェック
    const hasAccess = hasPermission(permission, userRole);
    
    // キャッシュに結果を保存
    permissionCache[permission] = hasAccess;
    
    return hasAccess;
  };

  // 権限キャッシュクリア関数
  const clearPermissionCache = () => {
    Object.keys(permissionCache).forEach(key => {
      delete permissionCache[key];
    });
  };

  // 中央集約されたユーザーロール設定関数
  const setUserRoleConsistently = (userData) => {
    if (!userData) {
      console.log('【ロール設定】ユーザーデータなし - GUEST設定');
      setUserRole(ROLES.GUEST);
      return;
    }

    let roleToSet = ROLES.VIEWER; // デフォルトロール
    
    if (userData.role) {
      roleToSet = mapBackendRoleToFrontend(userData.role);
      console.log('【ロール設定】バックエンドロールから変換:', {
        original: userData.role,
        mapped: roleToSet
      });
    } else {
      console.log('【ロール設定】ロール情報なし - デフォルトViewer設定');
    }
    
    setUserRole(roleToSet);
    
    // 権限キャッシュをクリア（ロール変更時）
    clearPermissionCache();
    
    console.log('【ロール設定】最終設定ロール:', roleToSet);
    return roleToSet;
  };

  // トークンリフレッシュ関数（依存配列の最適化）
  const refreshToken = async (onRefreshComplete = () => {}) => {
    if (isRefreshing) {
      // 既に実行中の場合は結果を待つ
      return new Promise((resolve) => {
        const checkRefreshComplete = () => {
          if (!isRefreshing) {
            resolve(tokenManager.getToken());
          } else {
            setTimeout(checkRefreshComplete, 100);
          }
        };
        checkRefreshComplete();
      });
    }

    isRefreshing = true;

    try {
      const refreshToken = localStorage.getItem('refreshToken');
      console.log('【デバッグ】リフレッシュトークン取得状況:', {
        tokenExists: !!refreshToken,
        tokenLength: refreshToken ? refreshToken.length : 0,
        tokenStart: refreshToken ? refreshToken.substring(0, 20) + '...' : 'なし',
        localStorageKeys: Object.keys(localStorage),
        authTokenExists: !!localStorage.getItem('authToken')
      });
      
      if (!refreshToken) {
        throw new Error('リフレッシュトークンがありません');
      }
      
      console.log('【デバッグ】トークンリフレッシュ開始');
      // JWTリフレッシュトークンをAuthorizationヘッダーで送信
      const response = await apiService.post('/auth/refresh', {}, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`
        }
      });
      
      console.log('【デバッグ】トークンリフレッシュレスポンス:', response.data);
      console.log('【デバッグ】トークンリフレッシュ成功 - 新しいアクセストークン取得');
      
      if (response.data && response.data.access_token) {
        const newToken = response.data.access_token;
        
        setToken(newToken);
        localStorage.setItem('authToken', newToken);
        
        // リフレッシュトークンも更新されている場合は保存
        if (response.data.refresh_token) {
          localStorage.setItem('refreshToken', response.data.refresh_token);
        }
        
        // APIサービスの認証ヘッダーを更新
        apiService.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        // 保留中のリクエストを再開
        onRefreshComplete(newToken);
        
        return newToken;
      } else {
        throw new Error('トークンの更新に失敗しました');
      }
    } catch (error) {
      console.error('【デバッグ】トークンリフレッシュエラー詳細:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      // リフレッシュ失敗時は認証情報をクリア
      clearAuthData();
      setToken(null);
      setCurrentUser(null);
      setUserRole(ROLES.GUEST);
      
      // エラーを設定
      setError('セッションの有効期限が切れました。再度ログインしてください。');
      setTokenExpired(true);
      
      return null;
    } finally {
      isRefreshing = false;
    }
  };

  // コンテキスト値をメモ化して不要な再レンダリングを防止
  const contextValue = React.useMemo(() => {
    const value = {
    currentUser,
    userRole,
    token,
    isLoggedIn,
    loading,
    error,
    tokenExpired,
    userPermissions,
    isInitialized,
    maintenanceMode,
    maintenanceEndTime,
    maintenanceContactUrl,
    login,
    logout,
    register,
    checkAuth,
    hasRole,
    checkPermission,
    refreshToken,
    clearPermissionCache,
    toggleMaintenanceMode,
    getMaintenanceState,
    fetchUserInfo
    };
    
    // 本番環境ではコンソールログを最小限に
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 AuthContext値更新:', {
        hasUser: !!currentUser,
        userRole,
        hasToken: !!token,
        isLoggedIn,
        loading,
        isInitialized
      });
    }
    
    return value;
  }, [
    currentUser, 
    userRole, 
    token, 
    isLoggedIn, 
    loading, 
    error, 
    tokenExpired, 
    userPermissions, 
    isInitialized,
    maintenanceMode,
    maintenanceEndTime,
    maintenanceContactUrl
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// カスタムフック
export const useAuth = () => {
  return useContext(AuthContext);
}; 